import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronLeft, ShieldCheck, Truck, Wrench, RotateCcw } from "lucide-react";
import {
  fetchProductByHandle,
  fetchProducts,
  formatPrice,
  type ShopifyProduct,
} from "@/lib/shopify";
import { AddToCartButton, BuyNowButton } from "@/components/shop/ProductCard";
import { RelatedProducts } from "@/components/shop/RelatedProducts";
import { CartDrawer } from "@/components/CartDrawer";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/butik/$handle")({
  loader: async ({ params }) => {
    const product = await fetchProductByHandle(params.handle);
    if (!product) throw notFound();
    let related: ShopifyProduct[] = [];
    try {
      const all = await fetchProducts(40);
      const type = product.node.productType;
      const others = all.filter((p) => p.node.handle !== product.node.handle);
      const same = others.filter((p) => type && p.node.productType === type);
      related = [...same, ...others.filter((p) => !same.includes(p))].slice(0, 4);
    } catch (e) {
      console.error("related products failed", e);
    }
    return { product, related };
  },

  head: ({ loaderData }: { loaderData?: { product: ShopifyProduct } }) => {
    const p = loaderData?.product.node;
    if (!p) return {};
    const url = `https://www.akhusbilfritid.se/butik/${p.handle}`;
    const desc = p.description.replace(/\s+/g, " ").slice(0, 155);
    const img = p.images.edges[0]?.node.url;
    return {
      meta: [
        { title: `${p.title.slice(0, 45)} – köp online | AK Husbil` },
        { name: "description", content: desc },
        { property: "og:title", content: p.title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        ...(img
          ? [
              { property: "og:image", content: img },
              { name: "twitter:image", content: img },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: p.title,
            description: desc,
            image: p.images.edges.map((e) => e.node.url),
            brand: { "@type": "Brand", name: "AK Husbil & Fritid" },
            offers: p.variants.edges.map((v) => ({
              "@type": "Offer",
              name: v.node.title,
              price: v.node.price.amount,
              priceCurrency: v.node.price.currencyCode,
              availability: v.node.availableForSale
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
              url,
            })),
          }),
        },
      ],
    };
  },
  component: ProductPage,
});

function ProductPage() {
  const { product, related } = Route.useLoaderData() as {
    product: ShopifyProduct;
    related: ShopifyProduct[];
  };
  const p = product.node;
  const variants = p.variants.edges.map((e) => e.node);
  const [variantId, setVariantId] = useState(variants[0]?.id ?? "");
  const variant = variants.find((v) => v.id === variantId) ?? variants[0];
  const [imgIdx, setImgIdx] = useState(0);
  const images = p.images.edges.map((e) => e.node);
  const main = images[imgIdx];
  const compareAt = variant?.compareAtPrice?.amount;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
      <div className="flex items-center justify-between gap-4">
        <Link
          to="/butik"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft size={16} /> Butik
        </Link>
        <CartDrawer />
      </div>

      <div className="mt-4 grid gap-8 lg:grid-cols-2">
        <div>
          <div className="aspect-square rounded-2xl border bg-muted overflow-hidden">
            {main && (
              <img
                src={main.url}
                alt={main.altText ?? p.title}
                width={1024}
                height={1024}
                fetchPriority="high"
                decoding="async"
                sizes="(max-width: 1024px) 100vw, 512px"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {images.map((im, i) => (
                <button
                  key={im.url}
                  onClick={() => setImgIdx(i)}
                  aria-label={`Bild ${i + 1}`}
                  className={`h-16 w-16 flex-shrink-0 rounded-lg overflow-hidden border ${
                    i === imgIdx ? "ring-2 ring-primary" : ""
                  }`}
                >
                  <img
                    src={im.url}
                    alt={im.altText ?? p.title}
                    width={64}
                    height={64}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {p.productType && (
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {p.productType}
            </span>
          )}
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight">{p.title}</h1>

          <div className="mt-4 flex items-baseline gap-3">
            <span className="text-2xl font-bold">
              {variant && formatPrice(variant.price.amount, variant.price.currencyCode)}
            </span>
            {compareAt && variant && parseFloat(compareAt) > parseFloat(variant.price.amount) && (
              <span className="text-muted-foreground line-through">
                {formatPrice(compareAt, variant.price.currencyCode)}
              </span>
            )}
          </div>

          <p className="mt-2 text-xs font-medium">
            {variant?.availableForSale ? (
              <span className="text-emerald-600 dark:text-emerald-400">
                ● I lager – skickas inom 1–2 arbetsdagar
              </span>
            ) : (
              <span className="text-muted-foreground">● Tillfälligt slut – hör av dig för ETA</span>
            )}
          </p>


          {variants.length > 1 && (
            <div className="mt-6">
              <p className="text-sm font-medium mb-2">
                {p.options[0]?.name ?? "Variant"}
              </p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVariantId(v.id)}
                    className={`min-h-11 rounded-xl border px-4 text-sm transition ${
                      v.id === variantId
                        ? "border-primary bg-primary/10 font-medium"
                        : "hover:bg-muted"
                    }`}
                  >
                    {v.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <BuyNowButton
              product={product}
              variantId={variantId}
              className="w-full sm:w-auto min-h-12 px-8"
              label="Köp nu – till kassan"
            />
            <AddToCartButton
              product={product}
              variantId={variantId}
              variant="outline"
              className="w-full sm:w-auto min-h-12 px-8"
            />
          </div>


          <div className="mt-6 grid gap-2 text-sm">
            <p className="flex items-start gap-2 text-muted-foreground">
              <Truck size={15} className="mt-0.5 shrink-0" /> Frakt i hela Sverige, spårbart – oftast
              1–2 arbetsdagar
            </p>
            <p className="flex items-start gap-2 text-muted-foreground">
              <RotateCcw size={15} className="mt-0.5 shrink-0" />
              <span>
                14 dagars ångerrätt och 3 års reklamationsrätt –{" "}
                <Link to="/kopvillkor" className="underline underline-offset-4 hover:text-foreground">
                  så funkar returer
                </Link>
              </span>
            </p>
            <p className="flex items-start gap-2 text-muted-foreground">
              <ShieldCheck size={15} className="mt-0.5 shrink-0" /> Utvalt och testat av vår verkstad
            </p>
            <p className="flex items-start gap-2 text-muted-foreground">
              <Wrench size={15} className="mt-0.5 shrink-0" /> Vi monterar gärna – boka tid i Kalix
            </p>
          </div>


          <div className="mt-8 prose prose-sm max-w-none text-foreground">
            {p.description.split("\n").filter(Boolean).map((line, i) => (
              <p key={i} className="text-sm leading-relaxed text-muted-foreground">
                {line}
              </p>
            ))}
          </div>

          <div className="mt-8 rounded-xl border bg-muted/30 p-4">
            <p className="text-sm">
              Behöver du en specifik reservdel istället?{" "}
              <Link to="/reservdelar" className="font-medium underline">
                Skicka en förfrågan
              </Link>{" "}
              – svar med pris inom 24 timmar.
            </p>
          </div>

          <div className="mt-4">
            <Button asChild variant="outline">
              <Link to="/butik">Se alla tillbehör</Link>
            </Button>
          </div>
        </div>
      </div>

      <RelatedProducts products={related} />
    </div>

  );
}
